// Local topic cache — the read path the auto-pull hook serves from. One JSON
// snapshot per workspace under ~/.driftless/cache/, kept fresh by delta-sync
// against GET /topics/sync (stale-while-revalidate; the hook never waits on
// the network when a snapshot exists). Cloud stays the source of truth and the
// entire write path — this is a read replica, deliberately plain JSON: at
// real workspace scale (tens to hundreds of topics) it loads in milliseconds,
// and the CLI stays dependency-free.
import { existsSync, readFileSync, writeFileSync, mkdirSync } from 'node:fs'
import { resolve, dirname } from 'node:path'
import { driftlessHome } from './api-client'
import { topicMatchesFile } from './glob-match'

export interface SyncResponse {
  cursor: string
  manifest: Array<{ slug: string; version: number; stale: boolean; stale_reason: string | null }>
  changed: Array<Record<string, unknown> & { topic: string }>
}

export interface TopicCache {
  cursor: string
  fetched_at: number
  topics: Record<string, any> // slug → CanonicalContext as served by /topics/sync
}

const sanitize = (s: string): string => s.replace(/[^a-zA-Z0-9_-]/g, '')

export function cachePath(workspaceSlug: string): string {
  return resolve(driftlessHome(), 'cache', `${sanitize(workspaceSlug)}.json`)
}

export function loadCache(workspaceSlug: string): TopicCache | null {
  try {
    const p = cachePath(workspaceSlug)
    if (!existsSync(p)) return null
    const c = JSON.parse(readFileSync(p, 'utf8')) as TopicCache
    if (!c || typeof c !== 'object' || !c.topics || !c.cursor) return null
    return c
  } catch {
    return null // a corrupt cache reads as "no cache" — the API fallback covers it
  }
}

export function saveCache(workspaceSlug: string, cache: TopicCache): void {
  try {
    const p = cachePath(workspaceSlug)
    mkdirSync(dirname(p), { recursive: true })
    writeFileSync(p, JSON.stringify(cache))
  } catch {
    /* best effort — next sync retries */
  }
}

// Merge a sync response into the existing snapshot. The manifest is the
// authority on EXISTENCE and DRIFT: topics absent from it are dropped
// (deleted/archived/orphaned server-side), and stale/stale_reason are patched
// in even when no body shipped (drift flips travel manifest-only). `changed`
// bodies upsert over whatever was stored.
export function applySync(prev: TopicCache | null, resp: SyncResponse, now: number): TopicCache {
  const topics: Record<string, any> = {}
  const inManifest = new Map(resp.manifest.map((m) => [m.slug, m]))
  for (const [slug, body] of Object.entries(prev?.topics ?? {})) {
    if (inManifest.has(slug)) topics[slug] = body
  }
  for (const body of resp.changed) {
    if (inManifest.has(body.topic)) topics[body.topic] = body
  }
  for (const [slug, m] of inManifest) {
    const body = topics[slug]
    if (!body) continue // manifest-only topic we never fetched a body for — next full sync seeds it
    body.stale = { ...(body.stale ?? {}), is_stale: m.stale, reason: m.stale_reason }
  }
  return { cursor: resp.cursor, fetched_at: now, topics }
}

// Self-heal detector. Two gaps are possible after a delta merge: a manifest
// topic with no local body (e.g. a private draft that became visible without a
// last_updated bump, or a corrupted file), and a body older than the manifest
// version (a write that raced the cursor read). Either answer is the same:
// the caller re-syncs WITHOUT a cursor (full snapshot) — cheap and total.
export function needsFullSync(cache: TopicCache, resp: SyncResponse): boolean {
  return resp.manifest.some((m) => {
    const body = cache.topics[m.slug]
    return !body || (typeof body.version === 'number' && body.version < m.version)
  })
}

// The cache-side equivalent of POST /topics/match-files: every cached topic
// whose anchors cover any of the given files.
export function matchCachedTopics(cache: TopicCache, files: string[]): any[] {
  const out: any[] = []
  for (const body of Object.values(cache.topics)) {
    if (files.some((f) => topicMatchesFile(body, f))) out.push(body)
  }
  return out
}
